import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BarChart2, TrendingUp, CheckCircle, Clock } from "lucide-react";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";

export default function Analytics() {
  const nav = useNavigate();
  const { current } = useWorkspace() || {};
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("7d");

  useEffect(() => {
    if (!current?.workspace_id) return;
    setLoading(true);
    api.get(`/workspaces/${current.workspace_id}/analytics`, { params: { period } })
      .then(({ data }) => setStats(data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [current?.workspace_id, period]);

  const StatCard = ({ icon: Icon, label, value, sub, color = "#5A90F5" }) => (
    <div className="border border-[#2A2A2A] rounded-lg p-4 bg-[#0c0c0c]">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="text-[10px] text-[#52525B] uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-bold text-[#EDEDED]">{value ?? "—"}</p>
      {sub && <p className="text-[10px] text-[#3F3F46] mt-1">{sub}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED]">
      <div className="border-b border-[#2A2A2A] px-6 py-4 flex items-center gap-3">
        <button onClick={() => nav(-1)} className="p-1.5 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <BarChart2 className="w-4 h-4 text-[#5A90F5]" />
        <h1 className="text-sm font-semibold">Workspace Analytics</h1>
        <div className="ml-auto flex gap-1">
          {["7d","30d","90d"].map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`px-2.5 py-1 rounded text-[11px] ${period === p ? "bg-[#5A90F5] text-white" : "text-[#A1A1AA] hover:bg-[#1a1a1a]"}`}>
              {p}
            </button>
          ))}
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-6 py-8">
        {loading ? (
          <p className="text-[#3F3F46] text-[12px]">Loading analytics…</p>
        ) : !stats ? (
          <p className="text-[#3F3F46] text-[12px]">No data available yet. Start using Crescendo to see insights here.</p>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-4 mb-8">
              <StatCard icon={CheckCircle} label="Nodes Completed" value={stats.completed} color="#22C55E" sub={`vs ${stats.completedPrev} prev period`} />
              <StatCard icon={Clock} label="Avg Duration" value={stats.avgDuration ? `${stats.avgDuration}m` : null} color="#14B8A6" sub="per node" />
              <StatCard icon={TrendingUp} label="Completion Rate" value={stats.rate ? `${stats.rate}%` : null} color="#A855F7" sub="of scheduled nodes" />
              <StatCard icon={BarChart2} label="Docs Created" value={stats.docs} color="#F59E0B" sub={`in last ${period}`} />
            </div>
            {stats.byDay && (
              <div className="border border-[#2A2A2A] rounded-lg p-4 bg-[#0c0c0c]">
                <p className="text-[10px] text-[#52525B] uppercase tracking-wider mb-3">Daily Completions</p>
                <div className="flex items-end gap-1 h-24">
                  {stats.byDay.map((d, i) => (
                    <div key={i} className="flex flex-col items-center gap-1 flex-1">
                      <div className="w-full bg-[#5A90F5] rounded-sm" style={{ height: `${Math.max(4, (d.count / (Math.max(...stats.byDay.map(x => x.count)) || 1)) * 80)}px` }} title={`${d.date}: ${d.count}`} />
                      <span className="text-[8px] text-[#3F3F46]">{d.date.slice(-2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
